import { apiClient } from './apiClient';

// 대시보드 통계 관련 인터페이스
export interface DashboardStats {
  newMemberCount: number;
  newQuestionsCount: number;
  activeMemberCount: number;
  courseStartCount: number;
  averageWatchTime: number;
}

export interface WatchTimeData {
  courseId: number;
  courseTitle: string;
  averageWatchTime: number;
}

export interface RatingData {
  courseId: number;
  courseTitle: string;
  averageRating: number;
  reviewCount: number;
}

// 강의별 응답 (x축: 절대 초)
export interface LectureRetentionApiResponse {
  lectureId: number;
  duration: number;
  bucketSeconds: number;
  viewerTotal: number;
  computedAt: string | null;
  points: { startSecond: number; retention: number; replayRatio: number }[] | null;
}

// 전체 평균 응답 (x축: 진행률 %)
export interface OverallRetentionApiResponse {
  binCount: number;
  lectureCount: number;
  viewerTotal: number;
  computedAt: string | null;
  points: { percent: number; retention: number; replayRatio: number }[] | null;
}

// 차트가 쓰는 형태. position 은 0~1 의 상대 위치라 두 축을 같은 코드로 그린다.
export interface WatchDurationPoint {
  position: number;
  label: string;
  percentage: number;
}

// 축 메타데이터. 눈금 라벨을 데이터 배열의 인덱스가 아니라
// 실제로 그려지는 x 위치(fraction)로부터 계산할 수 있게 해준다.
export type WatchDurationAxis =
  | { kind: 'seconds'; duration: number }
  | { kind: 'percent' };

export interface WatchDurationSeries {
  points: WatchDurationPoint[];
  axis: WatchDurationAxis;
  computedAt: string | null;
}

// 한 시간 미만은 m:ss, 그 이상은 h:mm:ss.
// 운영 최장 강의가 3,633초라 시(hour) 자리가 없으면 "60:33" 으로 찍힌다.
export const formatSecondsLabel = (seconds: number): string => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
    : `${minutes}:${String(remainingSeconds).padStart(2, '0')}`;
};

// 강의 목록 인터페이스
export interface LectureItem {
  id: number;
  title: string;
  category_name?: string;
  lecturer_name?: string;
  status?: boolean;
}

// 강의 목록 API 응답
export interface LectureListResponse {
  lectures: LectureItem[];
  totalCount: number;
}

// 대시보드 API 서비스
export const dashboardApi = {
  // 오늘의 통계 한번에 가져오기
  getTodayStats: async (): Promise<DashboardStats> => {
    const [
      newMemberCount,
      newQuestionsCount,
      activeMemberCount,
      courseStartCount,
      averageWatchTime
    ] = await Promise.all([
      apiClient.get('/api/admin/new-member-count/today'),
      apiClient.get('/api/admin/new-questions-count/today'),
      apiClient.get('/api/admin/active-member-count/today'),
      apiClient.get('/api/admin/course-start-count'),
      apiClient.get('/api/admin/average-watch-time/today')
    ]);

    return {
      newMemberCount: newMemberCount.data,
      newQuestionsCount: newQuestionsCount.data,
      activeMemberCount: activeMemberCount.data,
      courseStartCount: courseStartCount.data,
      averageWatchTime: averageWatchTime.data
    };
  },

  // 강의별 평균 시청 시간
  getAverageWatchTimeByCourse: async (): Promise<WatchTimeData[]> => {
    const response = await apiClient.get('/api/admin/average-watch-time/course');
    const raw = response.data?.data?.content
      ?? response.data?.content
      ?? response.data?.data
      ?? response.data
      ?? [];
    if (!Array.isArray(raw)) return [];
    // backend: lecture_id, lecture_title, avg_watch_time
    return raw.map((item: any) => ({
      courseId: item.lecture_id ?? item.courseId,
      courseTitle: item.lecture_title ?? item.courseTitle ?? '',
      averageWatchTime: item.avg_watch_time ?? item.averageWatchTime ?? 0,
    }));
  },

  // 강의별 평균 평점
  getAverageRatingByCourse: async (): Promise<RatingData[]> => {
    const response = await apiClient.get('/api/admin/average-rating/course');
    const raw = response.data?.data?.content
      ?? response.data?.content
      ?? response.data?.data
      ?? response.data
      ?? [];
    if (!Array.isArray(raw)) return [];
    // backend: lecture_id, lecture_title, avg_rating, rank
    return raw.map((item: any) => ({
      courseId: item.lecture_id ?? item.courseId,
      courseTitle: item.lecture_title ?? item.courseTitle ?? '',
      averageRating: item.avg_rating ?? item.averageRating ?? 0,
      reviewCount: item.rank ?? item.reviewCount ?? 0,
    }));
  },

  // 시청 지속률 - 전체 강의 (x축: 진행률)
  getWatchDurationOverall: async (): Promise<WatchDurationSeries> => {
    const response = await apiClient.get('/api/admin/average-continuous/course/overall');
    const apiData: OverallRetentionApiResponse = response.data.data;

    if (!apiData?.points || apiData.points.length === 0) {
      return {
        points: [],
        axis: { kind: 'percent' },
        computedAt: apiData?.computedAt ?? null,
      };
    }
    return {
      points: apiData.points.map(({ percent, retention }) => ({
        position: percent / 100,
        label: `${percent.toFixed(0)}%`,
        percentage: retention,
      })),
      axis: { kind: 'percent' },
      computedAt: apiData.computedAt ?? null,
    };
  },

  // 시청 지속률 - 특정 강의 (x축: 절대 초)
  getWatchDurationByLecture: async (
    lectureId: number,
    updateNow: boolean = false
  ): Promise<WatchDurationSeries> => {
    const updateParam = updateNow ? '?updateNow=true' : '';
    const response = await apiClient.get(
      `/api/admin/average-continuous/course/${lectureId}${updateParam}`
    );
    const apiData: LectureRetentionApiResponse = response.data.data;

    if (!apiData?.points || apiData.points.length === 0 || !apiData.duration) {
      return {
        points: [],
        axis: { kind: 'seconds', duration: apiData?.duration ?? 0 },
        computedAt: apiData?.computedAt ?? null,
      };
    }
    // x축은 응답에 들어온 최대 초가 아니라 강의 길이로 정규화한다.
    // 뒤쪽을 아무도 안 본 강의가 오른쪽 끝까지 꽉 찬 것처럼 그려지는 걸 막는다.
    return {
      points: apiData.points.map(({ startSecond, retention }) => ({
        position: startSecond / apiData.duration,
        label: formatSecondsLabel(startSecond),
        percentage: retention,
      })),
      axis: { kind: 'seconds', duration: apiData.duration },
      computedAt: apiData.computedAt ?? null,
    };
  },

  // 강의 목록 조회
  getLectureList: async (): Promise<LectureItem[]> => {
    /// console.log('dashboardApi: 강의 목록 API 호출');
    const response = await apiClient.get('/api/admin/lectures/list?page=0&size=1000000');
    /// console.log('dashboardApi: 원본 응답:', response);
    /// console.log('dashboardApi: response.data:', response.data);
    /// console.log('dashboardApi: response.data.content:', response.data?.content);
    /// console.log('dashboardApi: response.data.data:', response.data?.data);
    /// console.log('dashboardApi: response.data.data.content:', response.data?.data?.content);
    
    // 다양한 구조 시도
    const result = response.data?.data?.content || response.data?.content || response.data?.data || [];
    /// console.log('dashboardApi: 최종 반환값:', result);
    return result;
  },

  // 일일 활성 사용자 수 데이터 (기간별)
  getDailyActiveUsers: async (startDate: string, endDate: string): Promise<{ date: string; count: number }[]> => {
    const response = await apiClient.get(`/api/admin/active-member-count/today?startDate=${startDate}&endDate=${endDate}`);
    return response.data.data; // API 응답에서 data 필드 추출
  }
};